import { apiD2r, apiOms, apiWms } from './index';
import { cleanObject } from 'helper';
import { notification } from 'antd';
import { envs } from 'shared/utils/env';
import { MAX_DOWNLOAD_ORDER_LIMIT, MAX_DOWNLOAD_INVOICE_LIMIT, MAX_DOWNLOAD_PAYMENT_LIMIT } from '../../app/AppSeller/Orders/constants';
import errorHandler from '../utils/errorHandler';
import dayjs from 'dayjs';
import { EDOWNLOAD_ENTITY } from '../../constants';
import { v4 as uuidv4 } from 'uuid';
const { omsBaseUrl, d2rBaseUrl } = envs;

function getCompanyOrders({ current = 1, pageSize = 10, sortField = 'createdAt', sortOrder = 'DESC' }, filter, abortControllerRef = {}) {
	if (filter?.status?.length === 0) {
		delete filter.status;
	}
	if (abortControllerRef.current) {
		abortControllerRef.current.abort();
	}
	abortControllerRef.current = new AbortController();
	return apiOms.post(
		'/orders/find',
		{
			where: cleanObject({ ...filter }, ['', null, undefined]),
			pagination: {
				records: pageSize,
				pageNumber: current
			},
			sortBy: {
				order: sortField,
				by: sortOrder
			}
		},
		{
			signal: abortControllerRef.current.signal
		}
	);
}

function getCompanyMissedOrders({ current = 1, pageSize = 10, sortField = 'createdAt', sortOrder = 'DESC' }, filter, companyIds) {
	return apiOms.post('/orders/missed-orders', {
		pagination: {
			records: pageSize,
			pageNumber: current
		},
		sortBy: {
			order: sortField,
			by: sortOrder
		},
		where: cleanObject({ ...filter }, ['', null, undefined]),
		companyIds
	});
}

function syncMissedOrderForExternalStore(orderId, shopType, missedOrderId, companyId) {
	return apiOms.post('/order/sync-missed-order', {
		orderId,
		shopType,
		missedOrderId,
		companyId
	});
}

function getAllClusters() {
	return apiOms.get('/clusters/find');
}

async function downloadFillteredCSV(orderIDs) {
	try {
		const { data } = await apiOms.post(
			'/orders/csv',
			{
				orderID: orderIDs
			},
			{
				headers: {
					'x-idempotency-key': uuidv4()
				}
			},
			{ responseType: 'blob' }
		);
		const blob = new Blob([data], {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,'
		});
		let a = document.createElement('a');
		a.href = URL.createObjectURL(blob);
		a.download = `order${dayjs().format()}`;
		a.click();
	} catch (err) {
		errorHandler(err);
	}
}

const CONFIG = {
	invoices: {
		maxDownloadLimit: 100000,
		countKey: 'invoicesCount',
		limitVariable: MAX_DOWNLOAD_INVOICE_LIMIT
	},
	orders: {
		maxDownloadLimit: 200000,
		countKey: 'ordersCount',
		limitVariable: MAX_DOWNLOAD_ORDER_LIMIT
	},
	payments: {
		maxDownloadLimit: 200000,
		countKey: 'paymentsCount',
		limitVariable: MAX_DOWNLOAD_PAYMENT_LIMIT
	}
};

// download csv function for invoices and orders

async function downloadCSV(entityType, params = {}, orderIds = []) {
	const config = CONFIG[entityType];

	try {
		if (entityType === EDOWNLOAD_ENTITY.ORDERS && orderIds.length > 0) {
			downloadFillteredCSV(orderIds);
			return false;
		}

		for (const [key, value] of Object.entries(params)) {
			if (!value) delete params[key];
			if (key === 'filterFormValue' && entityType === EDOWNLOAD_ENTITY.ORDERS) {
				params[key] = JSON.stringify(value);
			}
			if (key === 'records' || (key === 'page' && entityType === EDOWNLOAD_ENTITY.INVOICES)) {
				delete params[key];
			}
		}

		const baseUrl = `${omsBaseUrl}/${entityType}`;

		const url = new URL(`${baseUrl}/csv`);
		const countUrl = new URL(`${baseUrl}/filter/count`);

		url.search = new URLSearchParams(params).toString();
		countUrl.search = new URLSearchParams(params).toString();

		const {
			data: { [config.countKey]: entityCount }
		} = await apiOms.get(countUrl);

		if (window.location.host?.includes('admin')) {
			if (entityCount > config.maxDownloadLimit) {
				throw new Error(`Too many ${entityType} to download. Apply more filters.`);
			}
		}

		if (entityCount > config.limitVariable) {
			return true;
		}

		window.open(url);
		return false;
	} catch (error) {
		notification.error({
			message: `Download ${entityType} Failed`,
			description: error instanceof Error ? error.message : 'Please try again later.',
			placement: 'topRight'
		});
		return false;
	}
}

// utility function used for download invoice and orders

async function downloadAndMail(type, params, email, socketId) {
	if (type !== EDOWNLOAD_ENTITY.PAYMENTS) {
		for (const [key, value] of Object.entries(params)) {
			if (!value) delete params[key];
			if (key === 'filterFormValue' && type === EDOWNLOAD_ENTITY.ORDERS) {
				params[key] = JSON.stringify(value);
			}
		}
	}
	const baseUrl = EDOWNLOAD_ENTITY.PAYMENTS === type ? d2rBaseUrl : omsBaseUrl;
	const mailUrl = new URL(`${baseUrl}/${type}/download/email`);
	mailUrl.search = new URLSearchParams(params);

	const { data } = await apiOms.post(mailUrl, {
		email,
		socketId
	});

	notification.success({
		message: `Download ${type}`,
		description: data?.message,
		placement: 'topRight'
	});

	return true;
}

async function downloadLablesAndMail(ids, email, type, socketId) {
	const { data } = apiOms.post('/download/bulk-label', {
		email: email,
		orderIds: ids,
		urlType: type === 'Label' ? 'shippingUrl' : 'invoiceUrl',
		socketId: socketId
	});

	notification.success({
		message: `Download ${type}s`,
		description: data?.message,
		placement: 'topRight'
	});

	return true;
}

function fetchOrderInfo(id, companyId) {
	if (companyId) {
		// For wms panel we need to pass companyId from panel
		return apiOms.get(`/order/${id}/${companyId}`);
	}
	return apiOms.get(`/order/${id}`);
}

function fetchOrderDriverInfo(orderId) {
	return apiOms.post(`/hyperlocal_data`, {
		orderId
	});
}

function setReadyToShip(id, couriorPartnerId) {
	return apiOms.post(`/readytoship/${id}`, {
		couriorPartnerId
	});
}

function setReadyToShipBulk(ids) {
	return apiWms.post('/order/processOrders', {
		orderIds: ids
	});
}

function downloadOrdersPickList(ids) {
	return apiOms.post(
		'/picklist',
		{
			orderIds: ids
		},
		{
			responseType: 'blob'
		}
	);
}

function updateOrderCustomerAddress(orderId, data) {
	return apiOms.put(`/order/customer-address/${orderId}`, data);
}

function updateOrderDetails(orderId, data) {
	return apiOms.put(`/order/details/${orderId}`, data);
}

function checkOrderServiceablity(orderId) {
	return apiOms.get(`/check-serviceability?orderId=${orderId}`);
}

function getPincodeDetails(pincode) {
	return apiOms.get(`/pincodeDetails/${pincode}`);
}

function cloneOrder(orderId) {
	return apiOms.post('/order/clone', {
		orderId: orderId
	});
}

function regenerateInvoiceAndLabel(orderId) {
	return apiOms.get(`/regenerate-invoice-and-label/${orderId}`);
}

function approveD2ROrder(orderId) {
	return apiOms.get(`/approve-d2r-order/${orderId}`);
}

function revertOrderStatusToPlaced(orderId) {
	return apiOms.get(`/revert-order-status-to-placed/${orderId}`);
}

export {
	getCompanyOrders,
	fetchOrderInfo,
	setReadyToShip,
	updateOrderCustomerAddress,
	downloadOrdersPickList,
	checkOrderServiceablity,
	getAllClusters,
	fetchOrderDriverInfo,
	getCompanyMissedOrders,
	syncMissedOrderForExternalStore,
	getPincodeDetails,
	downloadLablesAndMail,
	cloneOrder,
	regenerateInvoiceAndLabel,
	revertOrderStatusToPlaced,
	updateOrderDetails,
	approveD2ROrder,
	downloadAndMail,
	downloadCSV,
	downloadPaymentsCSV
};
